import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ChevronLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import { toast } from 'sonner';

export default function AdminHealthPlans() {
  const { api } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/admin/health-plans');
      setPlans(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Informe o nome do convênio');
      return;
    }

    try {
      await api.post('/admin/health-plans', { name: newName });
      toast.success('Convênio criado');
      setDialogOpen(false);
      setNewName('');
      fetchPlans();
    } catch (error) {
      toast.error('Erro ao criar convênio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover este convênio?')) return;

    try {
      await api.delete(`/admin/health-plans/${id}`);
      toast.success('Convênio removido');
      fetchPlans();
    } catch (error) {
      toast.error('Erro ao remover convênio');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-health-plans">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Convênios</h1>
            <p className="text-slate-600 mt-1">Gerenciar planos de saúde</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-primary">
            <Plus className="w-4 h-4 mr-2" />
            Novo Convênio
          </Button>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="animate-pulse h-20 bg-white rounded-2xl"></div>
            ))
          ) : plans.length > 0 ? (
            plans.map((plan) => (
              <div key={plan.id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{plan.name}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => handleDelete(plan.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          ) : (
            <div className="col-span-3 text-center py-16 bg-white rounded-2xl">
              <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum convênio encontrado</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Convênio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex: Unimed"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} className="bg-primary">
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
